import { useState } from "react";
import { ChevronDown } from "lucide-react";
import Reveal from "../components/Reveal";
import QuoteForm from "../components/QuoteForm";
import ClientMarquee from "../components/ClientMarquee";
import SEO from "../components/SEO";
import { serviceJsonLd } from "../data/seo";

const FAQS = [
  {
    q: "Why do video production quotes vary so much?",
    a: "Quote variation almost always comes down to what's included: planning time, the size of the crew, edit hours, music licensing, motion graphics, revisions and copyright assignment. A cheap quote usually drops one or more of these. Our quotes spell everything out so you can compare like for like.",
  },
  {
    q: "Do you charge by the hour or per project?",
    a: "We work on fixed-price projects. You'll know the exact total before we start, with no hidden extras. Bigger projects can be broken into agreed phases (pre-production, shoot, edit) if that helps cashflow.",
  },
  {
    q: "Is photography included with video production?",
    a: "Yes. Every video production package includes FREE stills photography on the day of the shoot. We bring a dedicated photographer alongside the video crew so you get a full library of imagery for social, web and print, worth around £800 on its own.",
  },
  {
    q: "How long does a video project take?",
    a: "Most projects are planned, filmed and edited in 2 to 4 weeks from sign-off. Urgent jobs can be turned around in days; complex animation and multi-shoot films take longer. Once you send the brief we'll confirm a realistic timeline with the fixed quote.",
  },
  {
    q: "Do you cover outside Liverpool?",
    a: "Yes, we're based in Liverpool city centre but cover the entire UK and Ireland. Travel is included in the fixed quote for jobs within a reasonable distance; longer trips are itemised transparently.",
  },
  {
    q: "Will I own the final video?",
    a: "Yes. The fixed quote includes unlimited copyright usage of every final exported file, so you can use the video on web, social, paid ads, broadcast and in perpetuity. We're also happy to sign NDAs for sensitive projects.",
  },
];

const faqJsonLd = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: FAQS.map((f) => ({
    "@type": "Question",
    name: f.q,
    acceptedAnswer: { "@type": "Answer", text: f.a },
  })),
};

function FaqItem({ q, a, idx }) {
  const [open, setOpen] = useState(idx === 0);
  return (
    <div className="border-b border-white/10" data-testid={`faq-item-${idx}`}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full flex justify-between items-center text-left py-5 group"
        aria-expanded={open}
        data-testid={`faq-toggle-${idx}`}
      >
        <span className="text-white text-lg font-bold pr-6 group-hover:text-[var(--yellow)] transition">{q}</span>
        <ChevronDown
          size={22}
          className={`text-[var(--yellow)] flex-shrink-0 transition-transform duration-300 ${open ? "rotate-180" : ""}`}
        />
      </button>
      <div
        className={`overflow-hidden transition-all duration-300 ease-out ${open ? "max-h-[400px] pb-5" : "max-h-0"}`}
      >
        <p className="text-white/75 leading-relaxed">{a}</p>
      </div>
    </div>
  );
}

export default function Prices() {
  return (
    <main className="pt-28 pb-12" data-testid="prices-page">
      <SEO
        title="Video Production Prices Liverpool | Quote in 4 Hours | Yellow"
        description="Transparent video production pricing in Liverpool. Send your brief and get a clean, fixed-price quote within 4 working hours, no obligation, no upsell."
        path="/video-production-prices"
        keywords="video production prices liverpool, video production cost liverpool, video production pricing liverpool, how much does a video cost liverpool, cheap video production liverpool, video production quote liverpool"
        jsonLd={[
          serviceJsonLd({
            name: "Video Production Prices Liverpool",
            description:
              "Competitive video production pricing in Liverpool with fixed-price quotes within 4 working hours.",
            path: "/video-production-prices",
          }),
          faqJsonLd,
        ]}
        breadcrumbs={[
          { name: "Home", path: "/" },
          { name: "Video Production Prices", path: "/video-production-prices" },
        ]}
      />
      <section className="yv-container">
        <Reveal>
          <p className="yv-eyebrow">Video Production Prices Liverpool</p>
          <h1 className="yv-h1 mt-3 max-w-5xl">
            <span style={{ color: "var(--yellow)" }}>Competitive video production prices.</span> Get a quote within 4 hours.
          </h1>
          <p className="text-white/70 max-w-3xl mt-6 text-lg leading-relaxed">
            Every project is unique, fill out the form below with as much detail as you can and we&apos;ll send a clean, fixed-price quote within 4 working hours. Below are the typical shapes our work takes.
          </p>
        </Reveal>
      </section>

      <ClientMarquee />

      <section className="yv-container !mt-20 max-w-3xl mx-auto">
        <Reveal>
          <QuoteForm heading="Let us know your requirements, quote within 4 hours" />
        </Reveal>
      </section>

      <section className="yv-container !mt-20 max-w-3xl mx-auto" data-testid="prices-faqs">
        <Reveal>
          <p className="yv-eyebrow">Pricing FAQs</p>
          <h2 className="yv-h2 text-white mt-2 mb-6">Common questions about cost.</h2>
        </Reveal>
        <div>
          {FAQS.map((f, i) => (
            <FaqItem key={f.q} idx={i} q={f.q} a={f.a} />
          ))}
        </div>
      </section>
    </main>
  );
}
